import React, {
  useEffect,
  useState,
  useRef,
  useCallback
} from "react";

import { useNavigate } from "react-router-dom";

import axios from "axios";


/* ============================================================
   API CONFIGURATION
   ============================================================ */

const RAW_API_URL =
  process.env.REACT_APP_API_URL ||
  "https://apimyshop.myidealtd.co.uk/api";


/*
 * Normalize API URL.
 *
 * Examples:
 *
 * https://apimyshop.myidealtd.co.uk
 *              ↓
 * https://apimyshop.myidealtd.co.uk/api
 *
 * https://apimyshop.myidealtd.co.uk/api
 *              ↓
 * https://apimyshop.myidealtd.co.uk/api
 *
 * https://apimyshop.myidealtd.co.uk
 *              ↓
 * https://apimyshop.myidealtd.co.uk/api
 *
 * https://apimyshop.myidealtd.co.uk/api/
 *              ↓
 * https://apimyshop.myidealtd.co.uk/api
 */

const API_URL = (() => {
  let url = String(RAW_API_URL).trim();

  // Remove trailing slash(es)
  url = url.replace(/\/+$/, "");

  // Add /api if it does not already exist
  if (!/\/api$/i.test(url)) {
    url = `${url}/api`;
  }

  return url;
})();


/*
 * Media URL does NOT contain /api.
 *
 * Example:
 *
 * API_URL
 * https://apimyshop.myidealtd.co.uk/api
 *
 * MEDIA_BASE_URL
 * https://apimyshop.myidealtd.co.uk
 */

const MEDIA_BASE_URL = API_URL.replace(
  /\/api\/?$/i,
  ""
);


/* ============================================================
   DEBUG INFORMATION
   ============================================================ */

console.log("====================================");
console.log("HeroCarousel configuration");
console.log("RAW_API_URL:", RAW_API_URL);
console.log("API_URL:", API_URL);
console.log("MEDIA_BASE_URL:", MEDIA_BASE_URL);
console.log(
  "BANNER_URL:",
  `${API_URL}/banners`
);
console.log("====================================");


/* ============================================================
   HERO CAROUSEL
   ============================================================ */

function HeroCarousel() {

  const navigate = useNavigate();


  /* ==========================================================
     STATE
     ========================================================== */

  const [slides, setSlides] = useState([]);

  const [loading, setLoading] = useState(true);

  const [index, setIndex] = useState(0);

  const [paused, setPaused] = useState(false);

  const [error, setError] = useState(false);


  const timerRef = useRef(null);


  /* ==========================================================
     BUILD MEDIA URL
     ========================================================== */

  const getMediaUrl = useCallback((media) => {

    if (!media) {
      return "";
    }


    const value = String(media).trim();


    if (!value) {
      return "";
    }


    /*
     * Already a complete URL
     *
     * https://example.com/image.jpg
     * http://example.com/image.jpg
     */

    if (
      value.startsWith("http://") ||
      value.startsWith("https://")
    ) {
      return value;
    }


    /*
     * Protocol-relative URL
     *
     * //example.com/image.jpg
     */

    if (value.startsWith("//")) {

      return `${window.location.protocol}${value}`;

    }


    /*
     * Remove leading slash(es)
     *
     * /uploads/banner.jpg
     * becomes
     * uploads/banner.jpg
     */

    const cleanPath = value.replace(
      /^\/+/,
      ""
    );


    /*
     * Build production/development media URL
     *
     * https://apimyshop.myidealtd.co.uk/uploads/banner.jpg
     */

    return `${MEDIA_BASE_URL}/${cleanPath}`;

  }, []);


  /* ==========================================================
     LOAD BANNERS
     ========================================================== */

  const loadBanners = useCallback(async () => {

    const bannerUrl =
      `${API_URL}/banners`;


    try {

      setLoading(true);

      setError(false);


      console.log(
        "HeroCarousel: Loading banners from:",
        bannerUrl
      );


      const response = await axios.get(
        bannerUrl,
        {
          timeout: 15000
        }
      );


      console.log(
        "HeroCarousel: Banner response:",
        response.data
      );


      /*
       * Support:
       *
       * response.data = [...]
       *
       * OR
       *
       * response.data = {
       *   banners: [...]
       * }
       */

      const bannerData =
        Array.isArray(response.data)

          ? response.data

          : Array.isArray(
              response.data?.banners
            )

            ? response.data.banners

            : [];


      setSlides(bannerData);

      setIndex(0);


    } catch (err) {

      console.error(
        "HeroCarousel: Failed to load banners"
      );


      console.error(
        "HeroCarousel Request URL:",
        bannerUrl
      );


      console.error(
        "HeroCarousel Status:",
        err.response?.status
      );


      console.error(
        "HeroCarousel Response:",
        err.response?.data
      );


      console.error(
        "HeroCarousel Error:",
        err
      );


      setSlides([]);

      setError(true);


    } finally {

      setLoading(false);

    }

  }, []);


  /* ==========================================================
     INITIAL LOAD
     ========================================================== */

  useEffect(() => {

    loadBanners();

  }, [loadBanners]);


  /* ==========================================================
     KEEP INDEX VALID
     ========================================================== */

  useEffect(() => {

    if (slides.length === 0) {

      setIndex(0);

      return;

    }


    if (index >= slides.length) {

      setIndex(0);

    }

  }, [
    slides.length,
    index
  ]);


  /* ==========================================================
     AUTO SLIDE
     ========================================================== */

  useEffect(() => {

    if (
      slides.length <= 1 ||
      paused
    ) {

      return;

    }


    timerRef.current =
      setInterval(() => {

        setIndex(
          (previousIndex) =>
            (
              previousIndex + 1
            ) % slides.length
        );

      }, 6000);


    return () => {

      if (timerRef.current) {

        clearInterval(
          timerRef.current
        );

        timerRef.current = null;

      }

    };

  }, [
    slides.length,
    paused
  ]);


  /* ==========================================================
     NEXT SLIDE
     ========================================================== */

  const nextSlide = () => {

    if (slides.length <= 1) {

      return;

    }


    setIndex(
      (previousIndex) =>
        (
          previousIndex + 1
        ) % slides.length
    );

  };


  /* ==========================================================
     PREVIOUS SLIDE
     ========================================================== */

  const previousSlide = () => {

    if (slides.length <= 1) {

      return;

    }


    setIndex(
      (previousIndex) =>
        (
          previousIndex -
          1 +
          slides.length
        ) % slides.length
    );

  };


  /* ==========================================================
     SELECT SLIDE
     ========================================================== */

  const selectSlide = (
    slideIndex
  ) => {

    setIndex(slideIndex);

  };


  /* ==========================================================
     BUTTON NAVIGATION
     ========================================================== */

  const handleButtonClick = (
    event,
    link
  ) => {

    event.stopPropagation();


    const destination =
      link || "/shop";


    /*
     * Internal React route
     */

    if (
      destination.startsWith("/")
    ) {

      navigate(destination);

      return;

    }


    /*
     * External URL
     */

    window.location.href =
      destination;

  };


  /* ==========================================================
     LOADING
     ========================================================== */

  if (loading) {

    return (

      <div className="hero-loading">

        <div
          className="hero-loading-spinner"
        />

        <span>
          Loading...
        </span>

      </div>

    );

  }


  /* ==========================================================
     NO BANNERS
     ========================================================== */

  if (
    error ||
    slides.length === 0
  ) {

    return null;

  }


  /* ==========================================================
     CURRENT SLIDE
     ========================================================== */

  const slide =
    slides[index] ||
    slides[0];


  const mediaUrl =
    getMediaUrl(
      slide?.media
    );


  const isVideo =
    String(
      slide?.mediaType || ""
    ).toLowerCase() === "video";


  /* ============================================================
     RENDER
     ============================================================ */

  return (

    <>

      <style>{`

        .hero-wrapper {
          position: relative;
          height: 560px;
          margin: 20px;
          border-radius: 35px;
          overflow: hidden;
          background: #111827;
          box-shadow:
            0 30px 80px rgba(0, 0, 0, .25);
        }


        .hero-media {
          position: absolute;
          inset: 0;
          width: 100%;
          height: 100%;
          object-fit: cover;

          animation:
            heroZoom 10s
            ease-in-out
            infinite
            alternate;
        }


        @keyframes heroZoom {

          0% {
            transform: scale(1);
          }

          100% {
            transform: scale(1.08);
          }

        }


        .hero-overlay {
          position: absolute;
          inset: 0;
          z-index: 1;

          background:
            linear-gradient(
              90deg,
              rgba(0, 0, 0, .78) 0%,
              rgba(0, 0, 0, .48) 45%,
              rgba(0, 0, 0, .12) 100%
            );
        }


        .hero-content {
          position: absolute;
          z-index: 3;
          top: 50%;
          left: 70px;

          transform:
            translateY(-50%);

          max-width: 650px;
          color: white;

          animation:
            heroSlideIn .8s ease;
        }


        @keyframes heroSlideIn {

          from {
            opacity: 0;
            transform:
              translateY(-40%);
          }

          to {
            opacity: 1;
            transform:
              translateY(-50%);
          }

        }


        .hero-title {
          margin: 0;

          font-size:
            clamp(
              2.2rem,
              5vw,
              4.5rem
            );

          font-weight: 900;

          line-height: 1.05;

          letter-spacing: -2px;

          text-shadow:
            0 4px 20px
            rgba(0, 0, 0, .35);
        }


        .hero-message {
          margin-top: 20px;
          margin-bottom: 0;

          font-size: 20px;

          line-height: 1.5;

          opacity: .92;

          max-width: 500px;

          text-shadow:
            0 2px 10px
            rgba(0, 0, 0, .35);
        }


        .hero-button {
          margin-top: 35px;

          padding:
            16px 38px;

          border-radius: 50px;

          border:
            1px solid
            rgba(255, 255, 255, .4);

          background:
            rgba(255, 255, 255, .15);

          backdrop-filter:
            blur(15px);

          color: white;

          font-size: 17px;

          font-weight: 800;

          cursor: pointer;

          transition:
            transform .3s ease,
            background .3s ease,
            color .3s ease;
        }


        .hero-button:hover {

          transform:
            translateY(-5px);

          background: white;

          color: #111827;

        }


        .hero-button:active {

          transform:
            translateY(-2px);

        }


        .hero-arrow {
          position: absolute;

          top: 50%;

          transform:
            translateY(-50%);

          z-index: 5;

          width: 50px;
          height: 50px;

          border-radius: 50%;

          border: none;

          background:
            rgba(255, 255, 255, .2);

          backdrop-filter:
            blur(10px);

          color: white;

          font-size: 28px;

          line-height: 1;

          cursor: pointer;

          transition:
            background .25s ease,
            transform .25s ease;
        }


        .hero-arrow:hover {

          background:
            rgba(255, 255, 255, .35);

          transform:
            translateY(-50%)
            scale(1.08);

        }


        .hero-arrow.left {

          left: 25px;

        }


        .hero-arrow.right {

          right: 25px;

        }


        .hero-dots {
          position: absolute;

          bottom: 25px;

          left: 50%;

          transform:
            translateX(-50%);

          display: flex;

          align-items: center;

          gap: 10px;

          z-index: 5;
        }


        .hero-dot {

          width: 10px;
          height: 10px;

          padding: 0;

          border: none;

          border-radius: 50%;

          background:
            rgba(255, 255, 255, .4);

          cursor: pointer;

          transition:
            width .25s ease,
            background .25s ease;
        }


        .hero-dot.active {

          width: 30px;

          border-radius: 20px;

          background: white;

        }


        .hero-loading {

          height: 500px;

          display: flex;

          align-items: center;

          justify-content: center;

          gap: 12px;

          font-size: 20px;

          color: #6b7280;

        }


        .hero-loading-spinner {

          width: 24px;
          height: 24px;

          border:
            3px solid #e5e7eb;

          border-top-color:
            #111827;

          border-radius: 50%;

          animation:
            heroSpinner
            .8s
            linear
            infinite;
        }


        @keyframes heroSpinner {

          to {
            transform:
              rotate(360deg);
          }

        }


        @media (max-width: 768px) {

          .hero-wrapper {

            height: 350px;

            margin: 12px;

            border-radius: 25px;

          }


          .hero-content {

            left: 25px;

            right: 25px;

            max-width: none;

          }


          .hero-title {

            font-size: 32px;

            letter-spacing: -1px;

          }


          .hero-message {

            font-size: 15px;

            max-width: 100%;

          }


          .hero-button {

            margin-top: 22px;

            padding:
              12px 25px;

            font-size: 15px;

          }


          .hero-arrow {

            display: none;

          }


          .hero-dots {

            bottom: 18px;

          }

        }


        @media (max-width: 480px) {

          .hero-wrapper {

            height: 300px;

            margin: 8px;

            border-radius: 20px;

          }


          .hero-content {

            left: 20px;

            right: 20px;

          }


          .hero-title {

            font-size: 27px;

          }


          .hero-message {

            margin-top: 12px;

            font-size: 14px;

            line-height: 1.4;

          }


          .hero-button {

            margin-top: 18px;

            padding:
              10px 20px;

            font-size: 14px;

          }

        }

      `}</style>


      <div
        className="hero-wrapper"

        onMouseEnter={() =>
          setPaused(true)
        }

        onMouseLeave={() =>
          setPaused(false)
        }
      >


        {/* ==================================================
            MEDIA
            ================================================== */}

        {isVideo ? (

          <video
            key={mediaUrl}

            className="hero-media"

            autoPlay

            muted

            loop

            playsInline

            preload="metadata"

            onError={() => {

              console.error(
                "Hero video failed:",
                mediaUrl
              );

            }}
          >

            <source
              src={mediaUrl}
              type="video/mp4"
            />

          </video>

        ) : (

          <img
            key={mediaUrl}

            src={mediaUrl}

            alt={
              slide?.title ||
              "Hero banner"
            }

            className="hero-media"

            loading="eager"

            onError={() => {

              console.error(
                "Hero image failed:",
                mediaUrl
              );

            }}
          />

        )}


        {/* ==================================================
            OVERLAY
            ================================================== */}

        <div
          className="hero-overlay"
        />


        {/* ==================================================
            PREVIOUS
            ================================================== */}

        {slides.length > 1 && (

          <button
            type="button"

            aria-label="Previous slide"

            className="hero-arrow left"

            onClick={(event) => {

              event.stopPropagation();

              previousSlide();

            }}
          >

            ‹

          </button>

        )}


        {/* ==================================================
            NEXT
            ================================================== */}

        {slides.length > 1 && (

          <button
            type="button"

            aria-label="Next slide"

            className="hero-arrow right"

            onClick={(event) => {

              event.stopPropagation();

              nextSlide();

            }}
          >

            ›

          </button>

        )}


        {/* ==================================================
            CONTENT
            ================================================== */}

        <div
          className="hero-content"

          key={
            slide?._id ||
            slide?.id ||
            index
          }
        >

          {slide?.title && (

            <h1
              className="hero-title"
            >
              {slide.title}
            </h1>

          )}


          {slide?.message && (

            <p
              className="hero-message"
            >
              {slide.message}
            </p>

          )}


          <button
            type="button"

            className="hero-button"

            onClick={(event) => {

              handleButtonClick(
                event,
                slide?.buttonLink
              );

            }}
          >

            {slide?.buttonText ||
              "Shop Now"}

            {" →"}

          </button>

        </div>


        {/* ==================================================
            DOTS
            ================================================== */}

        {slides.length > 1 && (

          <div
            className="hero-dots"
          >

            {slides.map(
              (slideItem, i) => (

                <button
                  key={
                    slideItem?._id ||
                    slideItem?.id ||
                    i
                  }

                  type="button"

                  aria-label={
                    `Go to slide ${i + 1}`
                  }

                  className={
                    `hero-dot ${
                      i === index
                        ? "active"
                        : ""
                    }`
                  }

                  onClick={(event) => {

                    event.stopPropagation();

                    selectSlide(i);

                  }}
                />

              )
            )}

          </div>

        )}

      </div>

    </>

  );

}


export default HeroCarousel;